// BattleInboxCard — small Dashboard card surfacing pending challenges,
// scheduled battles, and live matches the viewer is part of. Pulses when
// there's a challenge waiting on the viewer's response.
//
// Add-on. Hidden when there's nothing in any of the buckets so it doesn't
// add chrome to a quiet dashboard.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sword, Bell, Hourglass, Play, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useBattlesEnabled } from "./useBattlesEnabled";

const POLL_MS = 300_000; // 5 min — was 30s, caused visible flicker on the dashboard

export default function BattleInboxCard() {
  const { enabled } = useBattlesEnabled();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchInbox = async () => {
    try {
      const r = await api.get("/battles/inbox");
      setData(r.data);
    } catch { /* not fatal — feature flag could be off */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (!enabled) return;
    fetchInbox();
    const t = setInterval(fetchInbox, POLL_MS);
    return () => clearInterval(t);
  }, [enabled]);

  if (!enabled) return null;
  if (loading || !data) return null;
  const totalEntries =
    (data.awaiting_my_response?.length || 0) +
    (data.my_pending_outgoing?.length || 0) +
    (data.upcoming_scheduled?.length || 0) +
    (data.live_now?.length || 0);
  if (totalEntries === 0) return null;

  const hasActionable = (data.total_actionable || 0) > 0;

  return (
    <div
      className={`mt-8 rounded-xl border bg-white ${hasActionable ? "border-[#C16E5A] shadow-[0_0_0_4px_rgba(193,110,90,0.06)]" : "border-[#D8D6D2]"}`}
      data-testid="battle-inbox-card"
    >
      <div className="flex items-center justify-between p-4 border-b border-[#EFEDE8]">
        <div className="flex items-center gap-2">
          <Sword size={16} className="text-[#C16E5A]" />
          <h3 className="text-sm font-semibold tracking-tight text-[#1C1B1A]">Silo Wars inbox</h3>
          {hasActionable && (
            <span
              className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[#C16E5A] text-white text-[11px] font-medium animate-pulse"
              data-testid="battle-inbox-actionable-badge"
            >
              {data.total_actionable}
            </span>
          )}
        </div>
        <Link
          to="/wars"
          className="text-xs text-[#6B6A68] hover:text-[#1C1B1A] flex items-center gap-1"
          data-testid="battle-inbox-open-arena"
        >
          Arena <ArrowRight size={11} />
        </Link>
      </div>
      <div className="p-3 space-y-2">
        {data.live_now?.length > 0 && (
          <Section
            icon={Play}
            iconColor="#C16E5A"
            label={`Live now · ${data.live_now.length}`}
            battles={data.live_now}
            ctaLabel="Watch"
            testId="inbox-live"
          />
        )}
        {data.awaiting_my_response?.length > 0 && (
          <Section
            icon={Bell}
            iconColor="#C16E5A"
            label={`Waiting on you · ${data.awaiting_my_response.length}`}
            battles={data.awaiting_my_response}
            ctaLabel="Open"
            urgent
            testId="inbox-awaiting"
          />
        )}
        {data.upcoming_scheduled?.length > 0 && (
          <Section
            icon={Hourglass}
            iconColor="#A89360"
            label={`Scheduled · ${data.upcoming_scheduled.length}`}
            battles={data.upcoming_scheduled}
            ctaLabel="View"
            showSchedule
            testId="inbox-scheduled"
          />
        )}
        {data.my_pending_outgoing?.length > 0 && (
          <Section
            icon={Hourglass}
            iconColor="#6B6A68"
            label={`Awaiting opponent · ${data.my_pending_outgoing.length}`}
            battles={data.my_pending_outgoing}
            ctaLabel="View"
            testId="inbox-outgoing"
          />
        )}
      </div>
    </div>
  );
}

function Section({ icon: Icon, iconColor, label, battles, ctaLabel, urgent, showSchedule, testId }) {
  return (
    <div data-testid={testId}>
      <div className="flex items-center gap-1.5 mb-1.5">
        <Icon size={12} style={{ color: iconColor }} />
        <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">{label}</span>
      </div>
      <div className="space-y-1.5">
        {battles.slice(0, 3).map(b => (
          <BattleRow key={b.id} battle={b} ctaLabel={ctaLabel} urgent={urgent} showSchedule={showSchedule} />
        ))}
      </div>
    </div>
  );
}

function BattleRow({ battle, ctaLabel, urgent, showSchedule }) {
  const left = battle.challenger_silo;
  const right = battle.target_silo;
  return (
    <Link
      to={`/wars/arena/${battle.id}`}
      className={`flex items-center justify-between gap-3 p-2.5 rounded-md border transition ${urgent ? "border-[#C16E5A]/40 bg-[#C16E5A]/5 hover:bg-[#C16E5A]/10" : "border-[#EFEDE8] bg-white hover:bg-[#FAFAFA]"}`}
      data-testid={`inbox-row-${battle.id}`}
    >
      <div className="flex-1 min-w-0">
        <div className="text-sm text-[#1C1B1A] truncate">
          <strong>{left?.site_name}</strong>
          <span className="mx-1.5 text-[10px] uppercase tracking-widest text-[#A89360]">vs</span>
          <strong>{right?.site_name || "TBD"}</strong>
        </div>
        <div className="text-[10px] text-[#6B6A68] flex items-center gap-2 mt-0.5">
          {battle.is_broadcast === false && <span className="text-[#A89360]">🔒 Private</span>}
          {battle.is_rehearsal && <span className="text-[#A89360]">Rehearsal</span>}
          {battle.topic && <span className="truncate">on "{battle.topic}"</span>}
          {showSchedule && battle.scheduled_for && <span>· {new Date(battle.scheduled_for).toLocaleString()}</span>}
        </div>
      </div>
      <span className={`text-[11px] px-2 py-1 rounded font-medium ${urgent ? "bg-[#C16E5A] text-white" : "text-[#1C1B1A] border border-[#D8D6D2]"}`}>
        {ctaLabel}
      </span>
    </Link>
  );
}
